
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ProgressChartProps {
  data: Array<{
    date: string;
    value: number;
  }>;
}

export const ProgressChart = ({ data }: ProgressChartProps) => {
  const { language } = useLanguage();
  const { t } = useTranslations("student");
  const dateLocale = language === 'pt' ? ptBR : undefined;
  
  const formatDate = (date: string) => {
    return format(new Date(date), "dd/MM/yyyy", { locale: dateLocale });
  };

  if (!data || data.length === 0) {
    return (
      <Card className="shadow-md border border-slate-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-semibold dark:text-white">{t("overview.charts.progress")}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <p className="text-muted-foreground dark:text-gray-400">
            {t("overview.charts.noProgressData")}
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const customTooltip = (props: any) => {
    const { active, payload, label } = props;

    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border p-2 rounded-lg shadow-lg">
          <p className="text-sm text-muted-foreground">{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm">
              {t("overview.charts.progress")}: {entry.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <Card className="shadow-md border border-slate-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl font-semibold dark:text-white">{t("overview.charts.progress")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0,
              }}
            >
              <defs>
                <linearGradient id="colorProgress" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#DA5E29" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#DA5E29" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#474747" strokeOpacity={0.3} />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                tick={{ fill: "#E5E7EB" }}
                axisLine={{ stroke: '#555' }}
                tickMargin={10}
                className="text-xs dark:text-slate-400"
                tickFormatter={formatDate}
              />
              <YAxis 
                stroke="#9CA3AF" 
                tick={{ fill: "#E5E7EB" }}
                axisLine={{ stroke: '#555' }}
                className="text-xs dark:text-slate-400"
              />
              <Tooltip content={customTooltip} />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#DA5E29" 
                strokeWidth={3} 
                fillOpacity={1} 
                fill="url(#colorProgress)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
